/**
 * Transparency carousel.
 *
 * Moves the product named in a URL parameter to the front of the carousel and
 * marks it as highlighted.
 *
 * Liquid cannot read query strings — `request` exposes path, host, page_type,
 * design_mode, origin and locale only — so the match has to happen here.
 *
 * Reordering uses flex `order` rather than moving nodes, so the other cards are
 * never reflowed and the DOM order (and therefore tab order) stays stable.
 */
class TransparencyCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.highlightEnabled !== 'true') return;

    const param = this.dataset.highlightParam;
    if (!param) return;

    let wanted;
    try {
      wanted = new URLSearchParams(window.location.search).get(param);
    } catch {
      return;
    }
    if (!wanted) return;

    wanted = wanted.trim().toLowerCase();
    if (!wanted) return;

    const card = this.#findCard(wanted);
    if (!card) return;

    card.style.order = '-1';
    card.dataset.highlighted = 'true';

    const link = card.querySelector('a');
    if (link) link.setAttribute('aria-current', 'true');

    this.#reveal(card);
  }

  /**
   * Matches the product id first, then falls back to the handle, so both
   * ?product=8123456789 and ?product=beautiful-bold select the same card.
   *
   * @param {string} wanted lowercased parameter value
   * @returns {HTMLElement | undefined}
   */
  #findCard(wanted) {
    const cards = Array.from(this.querySelectorAll('[data-product-id], [data-product-handle]'));

    return (
      cards.find((card) => card.dataset.productId === wanted) ??
      cards.find((card) => (card.dataset.productHandle || '').toLowerCase() === wanted)
    );
  }

  /**
   * Scrolls the card into view inside the scroller only. `scrollIntoView` would
   * also scroll the page, jumping the visitor past whatever sits above.
   *
   * @param {HTMLElement} card
   */
  #reveal(card) {
    const scroller = card.parentElement;
    if (!scroller) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Wait a frame so the new `order` has been laid out before measuring.
    requestAnimationFrame(() => {
      const cardBox = card.getBoundingClientRect();
      const scrollerBox = scroller.getBoundingClientRect();
      const padding = parseFloat(getComputedStyle(scroller).paddingInlineStart) || 0;

      // Measured from the current scroll position and net of the scroller's own
      // padding, so a card that is already first lands at 0 and keeps the page
      // margin rather than being pulled flush to the viewport edge.
      const left = scroller.scrollLeft + (cardBox.left - scrollerBox.left) - padding;

      scroller.scrollTo({ left, behavior: reduced ? 'auto' : 'smooth' });
    });
  }
}

if (!customElements.get('transparency-carousel')) {
  customElements.define('transparency-carousel', TransparencyCarousel);
}
